import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, ExternalLink, Pause, Play, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { parseChordPro, CHORD_KEYS, semitoneDistance, transposeLine } from '@/chordpro/index';
import { ChordAnchoredLine, ChordRow, isChordOnly } from './ChordLines';

const MODE_LABELS = { musician: 'Muzikant', beamer: 'Beamer', print: 'Print', compact: 'Compact' };
const SPEEDS = { langzaam: 10, normaal: 20, snel: 36 };

const RenderLine = ({ line, mode, semitones, preferFlats, chordPosition }) => {
  if (line.isAnnotation) {
    // Arrangement/performance notes ("guitar only", "+ hammond") are meant
    // for musicians, never for the congregation looking at the beamer.
    if (mode === 'beamer') return null;
    return <p className="chordpro-annotation">{line.raw}</p>;
  }
  if (!line.raw.trim()) return mode === 'beamer' ? null : <div className="chordpro-spacer" aria-hidden="true" />;
  const rendered = transposeLine(line, semitones, preferFlats);
  if (mode === 'beamer') return <p className="chordpro-lyrics-only">{rendered.segments.map((part) => part.text).join('') || '\u00a0'}</p>;
  if (mode === 'compact') return isChordOnly(rendered) ? <ChordRow line={rendered} className="chordpro-compact-line" /> : <ChordAnchoredLine line={rendered} className="chordpro-compact-line chordpro-anchored" />;
  if (chordPosition === 'inline') {
    return <div className="chordpro-line chordpro-line-inline">{rendered.segments.map((part, index) => <span key={index} className="chordpro-inline-segment">{part.chord && <strong>{part.chord}</strong>}<span>{part.text || '\u00a0'}</span></span>)}</div>;
  }
  return isChordOnly(rendered) ? <ChordRow line={rendered} className="chordpro-line" /> : <ChordAnchoredLine line={rendered} className="chordpro-line chordpro-anchored" />;
};

const RenderSection = ({ section, mode, semitones, preferFlats, chordPosition }) => {
  const showHeader = section.type !== 'body';
  return <section className={`chordpro-section chordpro-section-${section.type}`}>
    {showHeader && <h3>{section.label || section.type}</h3>}
    {section.lines.map((line, lineIndex) => <RenderLine key={lineIndex} line={line} mode={mode} semitones={semitones} preferFlats={preferFlats} chordPosition={chordPosition} />)}
  </section>;
};

const ChordProRenderer = ({ source, initialKey, driveFileId, driveFolderId, sourceFileUrl, sourceFileName, pdfLinkUrl, pdfLinkLabel, initialMode = 'musician' }) => {
  const parsed = useMemo(() => parseChordPro(source || ''), [source]);
  const originalKey = parsed.metadata.key || initialKey || 'C';
  const requestedKey = initialKey || originalKey;
  const [targetKey, setTargetKey] = useState(requestedKey);
  const [mode, setMode] = useState(initialMode);
  const [speed, setSpeed] = useState('normaal');
  const [chordPosition, setChordPosition] = useState('above');
  const [scrolling, setScrolling] = useState(false);
  const viewportRef = useRef(null);
  const semitones = semitoneDistance(originalKey, targetKey);
  const preferFlats = targetKey.includes('b');

  useEffect(() => setTargetKey(requestedKey), [requestedKey]);
  useEffect(() => {
    if (!scrolling) return undefined;
    let last = performance.now();
    let frame;
    const tick = (now) => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      viewport.scrollTop += SPEEDS[speed] * ((now - last) / 1000);
      last = now;
      if (viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 2) setScrolling(false);
      else frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [scrolling, speed]);

  const fileId = driveFileId || parsed.metadata['x-zoea-drive'];
  const downloadSource = async () => {
    try {
      const url = sourceFileUrl?.startsWith('http') ? sourceFileUrl : `${process.env.REACT_APP_BACKEND_URL}${sourceFileUrl}`;
      const response = await axios.get(url, { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = sourceFileName || 'liedbron';
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      toast.error('Originele bron downloaden mislukt');
    }
  };
  return <div className={`chordpro-renderer chordpro-mode-${mode}`}>
    <div className="chordpro-renderer-toolbar print:hidden">
      <div className="flex flex-wrap gap-1">{Object.entries(MODE_LABELS).map(([value, label]) => <Button key={value} type="button" size="sm" variant={mode === value ? 'default' : 'outline'} onClick={() => setMode(value)}>{label}</Button>)}</div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-slate-500">Toonsoort <select className="ml-1 h-8 rounded border border-slate-200 bg-white px-2" value={targetKey} onChange={(event) => setTargetKey(event.target.value)}>{CHORD_KEYS.map((key) => <option key={key}>{key}</option>)}</select></label>
        <select aria-label="Autoscroll snelheid" className="h-8 rounded border border-slate-200 bg-white px-2 text-xs" value={speed} onChange={(event) => setSpeed(event.target.value)}>{Object.keys(SPEEDS).map((value) => <option key={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}</select>
        <div className="flex items-center rounded border border-slate-200 bg-white p-0.5" aria-label="Akkoordpositie">
          <Button type="button" size="sm" variant={chordPosition === 'above' ? 'default' : 'ghost'} onClick={() => setChordPosition('above')}>Boven</Button>
          <Button type="button" size="sm" variant={chordPosition === 'inline' ? 'default' : 'ghost'} onClick={() => setChordPosition('inline')}>Inline</Button>
        </div>
        <Button type="button" size="icon" variant="outline" title={scrolling ? 'Pauze' : 'Autoscroll starten'} onClick={() => setScrolling((value) => !value)}>{scrolling ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</Button>
        <Button type="button" size="icon" variant="outline" title="Afdrukken of opslaan als PDF" onClick={() => window.print()}><Printer className="h-4 w-4" /></Button>
        {sourceFileUrl && <Button type="button" size="icon" variant="outline" title="Download originele importbron" onClick={downloadSource}><Download className="h-4 w-4" /></Button>}
        {fileId && <a href={`https://drive.google.com/file/d/${encodeURIComponent(fileId)}/view`} target="_blank" rel="noreferrer"><Button type="button" size="icon" variant="outline" title="Open origineel in Google Drive"><ExternalLink className="h-4 w-4" /></Button></a>}
        {fileId && <a href={`https://drive.google.com/uc?export=download&id=${encodeURIComponent(fileId)}`}><Button type="button" size="icon" variant="outline" title="Download origineel"><Download className="h-4 w-4" /></Button></a>}
        {!fileId && driveFolderId && <a href={`https://drive.google.com/drive/folders/${encodeURIComponent(driveFolderId)}`} target="_blank" rel="noreferrer"><Button type="button" size="icon" variant="outline" title="Open Google Drive-map"><ExternalLink className="h-4 w-4" /></Button></a>}
        {pdfLinkUrl && <a href={pdfLinkUrl} target="_blank" rel="noreferrer"><Button type="button" variant="outline" size="sm" title={pdfLinkLabel || 'Bekijk PDF'}><ExternalLink className="mr-1 h-4 w-4" />{pdfLinkLabel || 'Bekijk PDF'}</Button></a>}
      </div>
    </div>
    <div ref={viewportRef} className="chordpro-renderer-viewport">
      <header className="mb-7 border-b border-slate-200 pb-4"><h2>{parsed.metadata.title || 'Naamloos lied'}</h2><p>{[parsed.metadata.artist, targetKey && `Toonsoort ${targetKey}`, parsed.metadata.tempo && `${parsed.metadata.tempo} BPM`].filter(Boolean).join(' · ')}</p></header>
      {parsed.sections.map((section, sectionIndex) => <RenderSection key={`${section.type}-${sectionIndex}`} section={section} mode={mode} semitones={semitones} preferFlats={preferFlats} chordPosition={chordPosition} />)}
      {!parsed.sections.length && <p className="text-slate-500">Nog geen liedtekst beschikbaar.</p>}
    </div>
  </div>;
};

export default ChordProRenderer;
